/**
 * The one place that decides a market's maximum leverage.
 *
 * Only the venue knows it: `max_leverage` from `/markets`. When that is missing
 * the leverage modal used to cap at 1x while the trade form fell back to a
 * client-side table, so the two disagreed and the table could promise leverage
 * the venue rejects. Now both treat it as UNKNOWN: `undefined`, and the UI says
 * "Max leverage unavailable" and does not let the leverage change.
 */
export const resolveMaxLeverage = (
  instrument?: { max_leverage?: unknown } | null
): number | undefined => {
  const raw = instrument?.max_leverage;
  if (raw === undefined || raw === null || raw === "") return undefined;
  const lev = Number(raw);
  return Number.isFinite(lev) && lev >= 1 ? lev : undefined;
};

/** A positive integer leverage from an account value, defaulting to 1x. */
export const normalizeAccountLeverage = (value: unknown): number => {
  const lev = Number(value);
  return Number.isFinite(lev) && lev > 0 ? Math.max(1, Math.round(lev)) : 1;
};

/**
 * The cap every leverage clamp uses: the venue's maximum when known, otherwise
 * the leverage the account already has, so an unknown maximum can never move
 * the leverage anywhere.
 */
export const leverageCap = (
  maxLeverage: number | undefined,
  accountLeverage: unknown
): number => maxLeverage ?? normalizeAccountLeverage(accountLeverage);
